import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import {
  changeModuleIndex,
  reorderModifiers,
  reorderModules,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import DraggableList, { DraggableListContent } from 'src/presentation/components/pages/shared/DraggableList';
import ModuleComponent from './ThreadBuilder/ModuleComponent';
import { NextData } from 'src/models/Sidekick/SidekickThreads';
import useRouter from 'src/presentation/hooks/useRouter';
import AlertsPanel from './ThreadBuilder/AlertsPanel';

const ThreadBuilder = ({
  actions,
  attr,
  disabled,
  isAnon,
  modules,
  showAddModuleButton,
}: {
  actions?: ReactNode;
  attr: string;
  disabled: boolean;
  isAnon?: boolean;
  modules: NextData[];
  showAddModuleButton: boolean;
}) => {
  const dispatch = useDispatch();
  const { history } = useRouter();

  const [options, setOptions] = useState(modules);

  useEffect(() => {
    setOptions(modules);
  }, [modules, setOptions]);

  const handleDragEnd = result => {
    if (result.type === 'module-droppable:module') {
      dispatch(reorderModules(result));
      return;
    }
    dispatch(reorderModifiers(result));
  };

  const handleClick = item => {
    const moduleIndex = options.findIndex(i => i.id === item.id);
    dispatch(changeModuleIndex({ moduleIndex, history, modifierIndex: null }));
  };

  const handleModifierClick = ({ module, modifier }) => {
    const moduleIndex = options.findIndex(i => i.id === module.id);
    const modifierIndex = module.modifierValues.findIndex(i => i.id === modifier.id);

    dispatch(changeModuleIndex({ moduleIndex, history, modifierIndex }));
  };

  return (
    <PanelWrapper data-aid="thread-builder-container">
      {actions && <Actions>{actions}</Actions>}
      <AlertsPanel />
      <DraggableListContent onDragEnd={handleDragEnd}>
        <DraggableList
          disabled={disabled}
          items={options}
          renderRow={props => (
            <ModuleItem
              {...props}
              attr={isAnon ? attr : `${attr}[${props.index}]`}
              disabled={disabled}
              showAddModuleButton={showAddModuleButton}
              onClick={handleClick}
              onModifierClick={handleModifierClick}
            />
          )}
          droppableId="module-droppable"
          type="module"
        />
      </DraggableListContent>
    </PanelWrapper>
  );
};

const ModuleItem = ({
  attr,
  disabled,
  item,
  provided,
  snapshot,
  onClick,
  onModifierClick,
  showAddModuleButton,
}: {
  attr: string;
  disabled: boolean;
  item: NextData;
  provided;
  snapshot;
  onClick;
  onModifierClick;
  showAddModuleButton: boolean;
}) => {
  const handleClick = evn => {
    evn.preventDefault();
    onClick && onClick(item);
  };

  const handleModifierClick = modifier => {
    onModifierClick && onModifierClick({ module: item, modifier });
  };

  if (!item) return null;

  return (
    <>
      <StyledModuleItemContainer>
        <ModuleComponent
          attr={attr}
          disabled={disabled}
          item={item}
          handleClick={handleClick}
          handleModifierClick={handleModifierClick}
          provided={provided}
          snapshot={snapshot}
          showAddModuleButton={showAddModuleButton}
        />
      </StyledModuleItemContainer>
    </>
  );
};

const Actions = styled.div`
  padding: 24px 0;
`;

export const StyledItem = styled.div`
  margin-right: auto;
`;

const StyledModuleItemContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PanelWrapper = styled.div`
  padding: 12px 24px 0 24px;
`;

export default ThreadBuilder;
